// plan-16 S02: virtual filesystem over library-index.json
// Provides list, read, stat, resolve operations over a read-only virtual FS.
// Tree: / (root) → README.txt, oracle.txt, VPK_SERIES/ → seasons → episodes

import { resolve, basename, dirname } from './path'
import { README_CONTENT, ORACLE_CONTENT } from './authored'

export type FsKind = 'file' | 'dir'

export interface FsNode {
  name: string
  kind: FsKind
  size: string // human-readable
  sizeBytes: number
  displayName: string
}

export interface FsFileNode extends FsNode {
  kind: 'file'
  ext: string
  urlPath: string // /library/<slug-path>
  durationSec: number | null
  posterSlug: string | null
}

export interface FsDirNode extends FsNode {
  kind: 'dir'
  childCount: number
}

interface FsError {
  error: string
}

type FsResult<T> = T | FsError

// Index data structures (from library-index.json)
interface IndexEntry {
  k: 'file' | 'dir'
  n: string // displayName
  sz?: number // size bytes (file only)
  sl?: string // size label (file only)
  s?: string // size label (dir only)
  d?: number // duration sec (file only, optional)
  p?: string // poster slug (file only, optional)
  c?: string[] // child slugs (dir only)
}

let indexLoaded = false
let indexData: Record<string, IndexEntry> = {}

async function loadIndex(): Promise<void> {
  if (indexLoaded) return
  try {
    const resp = await fetch('/library-index.json')
    if (resp.ok) {
      const json = await resp.json()
      indexData = json.byPath ?? {}
    }
  } catch {
    // offline — FS still has authored content
  }
  indexLoaded = true
}

// Root directory children (always present)
function getRootChildren(): Map<string, FsNode> {
  const children = new Map<string, FsNode>()
  children.set('README.txt', {
    name: 'README.txt',
    kind: 'file',
    size: `${README_CONTENT.length} B`,
    sizeBytes: README_CONTENT.length,
    displayName: 'README.txt',
    ext: '.txt',
    urlPath: '',
    durationSec: null,
    posterSlug: null,
  } as FsFileNode)
  children.set('oracle.txt', {
    name: 'oracle.txt',
    kind: 'file',
    size: `${ORACLE_CONTENT.length} B`,
    sizeBytes: ORACLE_CONTENT.length,
    displayName: 'oracle.txt',
    ext: '.txt',
    urlPath: '',
    durationSec: null,
    posterSlug: null,
  } as FsFileNode)
  children.set('VPK_SERIES', {
    name: 'VPK_SERIES',
    kind: 'dir',
    size: '(4 items)',
    sizeBytes: 0,
    displayName: 'VPK_SERIES',
    childCount: 4,
  } as FsDirNode)
  return children
}

// Get children of /VPK_SERIES (top level: 4 season dirs + 4 OVA files)
function getVpkChildren(): Map<string, FsNode> {
  const children = new Map<string, FsNode>()
  // Look for entries directly under root (one path segment)
  for (const [path, entry] of Object.entries(indexData)) {
    const parts = path.split('/').filter(Boolean)
    if (parts.length === 1) {
      // Root-level entry (OVA files or season dirs)
      children.set(entry.n, entryToNode(entry, path))
    }
  }
  return children
}

function entryToNode(entry: IndexEntry, path: string): FsNode {
  if (entry.k === 'dir') {
    return {
      name: entry.n,
      kind: 'dir',
      size: entry.s ?? `${(entry.c ?? []).length} items`,
      sizeBytes: 0,
      displayName: entry.n,
      childCount: (entry.c ?? []).length,
    } as FsDirNode
  }
  return {
    name: entry.n,
    kind: 'file',
    size: entry.sl ?? `${entry.sz ?? 0} B`,
    sizeBytes: entry.sz ?? 0,
    displayName: entry.n,
    ext: '.mp4',
    urlPath: `/library${path}`,
    durationSec: entry.d ?? null,
    posterSlug: entry.p ?? null,
  } as FsFileNode
}

// Find the index entry path that matches a virtual FS path
// Virtual FS paths: /VPK_SERIES/S01 - THE KING'S MATHEMAGICKS/EP01 - 48.mp4
// Index paths: /S01-the-kings-mathemagicks/EP01-48.mp4
function findIndexPath(virtualPath: string): string | null {
  // Special paths
  if (virtualPath === '' || virtualPath === '/') return null
  if (virtualPath === '/README.txt' || virtualPath === '/oracle.txt') return null
  if (virtualPath === '/VPK_SERIES') return null

  // Must start with /VPK_SERIES/
  const rest = virtualPath.startsWith('/VPK_SERIES/') ? virtualPath.slice('/VPK_SERIES/'.length) : null
  if (!rest) return null

  // Try to match by traversing the index
  // First, check if it's a direct match under /VPK_SERIES/
  // The display name in the index matches the original name
  for (const [idxPath, entry] of Object.entries(indexData)) {
    if (entry.n === rest || entry.n === rest.replace(/\.mp4$/, '')) {
      return idxPath
    }
  }

  // Try matching by slug
  // Slugify the rest and look it up
  const slug = slugify(rest)
  if (indexData['/' + slug]) return '/' + slug

  return null
}

function slugify(name: string): string {
  return name
    .replace(/['']/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_.]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export async function init(): Promise<void> {
  await loadIndex()
}

export async function list(absPath: string): Promise<FsResult<FsNode[]>> {
  await loadIndex()
  const n = absPath === '' ? '/' : absPath

  if (n === '/') {
    return Array.from(getRootChildren().values())
  }
  if (n === '/VPK_SERIES') {
    return Array.from(getVpkChildren().values())
  }

  // Season directory: /VPK_SERIES/<display-name>
  const rest = n.startsWith('/VPK_SERIES/') ? n.slice('/VPK_SERIES/'.length) : null
  if (!rest) return { error: `no such directory: ${n}` }

  // Find matching season dir in index
  for (const [idxPath, entry] of Object.entries(indexData)) {
    if (entry.k !== 'dir') continue
    if (entry.n === rest) {
      const children: FsNode[] = []
      for (const childSlug of (entry.c ?? [])) {
        const childPath = idxPath + '/' + childSlug
        const childEntry = indexData[childPath]
        if (childEntry) {
          children.push(entryToNode(childEntry, childPath))
        }
      }
      return children
    }
  }

  return { error: `no such directory: ${n}` }
}

export async function read(absPath: string): Promise<FsResult<string>> {
  const n = absPath === '' ? '/' : absPath
  if (n === '/README.txt') return README_CONTENT
  if (n === '/oracle.txt') return ORACLE_CONTENT

  const rest = n.startsWith('/VPK_SERIES/') ? n.slice('/VPK_SERIES/'.length) : null
  if (!rest) return { error: `no such file: ${n}` }

  // Check if it's an mp4 file (binary)
  const idxPath = findIndexPath(n)
  if (idxPath && indexData[idxPath]?.k === 'file') {
    return { error: 'binary' }
  }

  return { error: `no such file: ${n}` }
}

export async function stat(absPath: string): Promise<FsResult<FsNode>> {
  await loadIndex()
  const n = absPath === '' ? '/' : absPath

  if (n === '/') {
    return { name: '/', kind: 'dir', size: '3 items', sizeBytes: 0, displayName: '/', childCount: 3 } as FsDirNode
  }

  // Authored files
  if (n === '/README.txt' || n === '/oracle.txt') {
    const content = n === '/README.txt' ? README_CONTENT : ORACLE_CONTENT
    return {
      name: basename(n),
      kind: 'file',
      size: `${content.length} B`,
      sizeBytes: content.length,
      displayName: basename(n),
      ext: '.txt',
      urlPath: '',
      durationSec: null,
      posterSlug: null,
    } as FsFileNode
  }

  if (n === '/VPK_SERIES') {
    return {
      name: 'VPK_SERIES',
      kind: 'dir',
      size: '(4 items)',
      sizeBytes: 0,
      displayName: 'VPK_SERIES',
      childCount: 4,
    } as FsDirNode
  }

  // Look up in index
  const idxPath = findIndexPath(n)
  if (idxPath && indexData[idxPath]) {
    return entryToNode(indexData[idxPath], idxPath)
  }

  return { error: `no such file or directory: ${n}` }
}

export function isDir(node: FsNode): node is FsDirNode {
  return node.kind === 'dir'
}

export function isFile(node: FsNode): node is FsFileNode {
  return node.kind === 'file'
}

export function isVideo(node: FsNode): boolean {
  return node.kind === 'file' && 'ext' in node && (node as FsFileNode).ext === '.mp4'
}

// Resolve a virtual path from the user's perspective (display names, not slugs)
// This is used by the shell commands to navigate the tree using display names
export { resolve, basename, dirname }
